import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { defaultQueryFn } from "@/lib/api-client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { ProtectedRoute, ActiveMerchantRoute, NonEmptyCartRoute, BackofficeRoute, ProfileRoute } from "@/components/routing/guards";

import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import POSHome from "./pages/pos/POSHome";
import POSCart from "./pages/pos/POSCart";
import Checkout from "./pages/pos/Checkout";
import Receipt from "./pages/pos/Receipt";
import MurabahaFlow from "./pages/pos/MurabahaFlow";
import Shift from "./pages/pos/Shift";
import SalesList from "./pages/sales/SalesList";
import Products from "./pages/products/Products";
import Categories from "./pages/categories/Categories";
import PriceLists from "./pages/pricing/PriceLists";
import Inventory from "./pages/inventory/Inventory";
import Branches from "./pages/branches/Branches";
import ProfilePage from "./pages/profile/ProfilePage";
import MurabahaQueue from "./pages/murabaha/MurabahaQueue";
import MurabahaDetail from "./pages/murabaha/MurabahaDetail";
import Reports from "./pages/reports/Reports";

import Connectors from "./pages/connectors/Connectors";
import ModulesPage from "./pages/modules/ModulesPage";
import ModulePlaceholder from "./pages/modules/ModulePlaceholder";

import Customers from "./pages/customers/Customers";
import CustomerProfile from "./pages/customers/CustomerProfile";
import AuditLogs from "./pages/audit/AuditLogs";
import Tickets from "./pages/tickets/Tickets";

import AdminLayout from "./components/layout/AdminLayout";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminMerchants from "./pages/admin/AdminMerchants";
import AdminMerchantDetail from "./pages/admin/AdminMerchantDetail";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminContracts from "./pages/admin/AdminContracts";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminFiscalReceipts from "./pages/admin/AdminFiscalReceipts";
import AdminMerchantPayouts from "./pages/admin/AdminMerchantPayouts";
import AdminContractDetail from "./pages/admin/AdminContractDetail";
import RequireAdminRole from "./components/admin/RequireAdminRole";

import AdminTickets from "./pages/admin/AdminTickets";
import AdminTicketDetail from "./pages/admin/AdminTicketDetail";
import AdminReports from "./pages/admin/AdminReports";
import AdminMerchantCreate from "./pages/admin/AdminMerchantCreate";
import AdminPlatformUsers from "./pages/admin/AdminPlatformUsers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminErrors from "./pages/admin/AdminErrors";
import AdminMarketingDashboard from "./pages/admin/marketing/AdminMarketingDashboard";
import AdminMarketingCampaigns from "./pages/admin/marketing/AdminMarketingCampaigns";
import AdminMarketingCampaignDetail from "./pages/admin/marketing/AdminMarketingCampaignDetail";
import AdminMarketingApplications from "./pages/admin/marketing/AdminMarketingApplications";
import AdminMarketingCashback from "./pages/admin/marketing/AdminMarketingCashback";
import AdminMarketingCampaignNew from "./pages/admin/marketing/AdminMarketingCampaignNew";
import AdminMarketingPlacement from "./pages/admin/marketing/AdminMarketingPlacement";
import AdminMarketingCalendar from "./pages/admin/marketing/AdminMarketingCalendar";
import AdminMarketingHistory from "./pages/admin/marketing/AdminMarketingHistory";
import AdminMarketingSettings from "./pages/admin/marketing/AdminMarketingSettings";
import MarketingPage from "./pages/marketing/MarketingPage";
import ErrorBoundary from "./components/ErrorBoundary";



import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 30_000,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* Backoffice routes */}
              <Route path="/dashboard" element={<BackofficeRoute><Dashboard /></BackofficeRoute>} />
              <Route path="/sales" element={<BackofficeRoute><SalesList /></BackofficeRoute>} />
              <Route path="/products" element={<BackofficeRoute><Products /></BackofficeRoute>} />
              <Route path="/categories" element={<BackofficeRoute><Categories /></BackofficeRoute>} />
              <Route path="/pricing" element={<BackofficeRoute><PriceLists /></BackofficeRoute>} />
              <Route path="/inventory" element={<BackofficeRoute><Inventory /></BackofficeRoute>} />
              <Route path="/branches" element={<Navigate to="/profile" replace />} />
              <Route path="/profile" element={<ProfileRoute><ProfilePage /></ProfileRoute>} />
              <Route path="/murabaha" element={<BackofficeRoute><MurabahaQueue /></BackofficeRoute>} />
              <Route path="/murabaha/:id" element={<BackofficeRoute><MurabahaDetail /></BackofficeRoute>} />
              <Route path="/reports" element={<BackofficeRoute><Reports /></BackofficeRoute>} />
              
              <Route path="/connectors" element={<BackofficeRoute><Connectors /></BackofficeRoute>} />
              <Route path="/modules" element={<BackofficeRoute><ModulesPage /></BackofficeRoute>} />
              <Route path="/modules/:code" element={<BackofficeRoute><ModulePlaceholder /></BackofficeRoute>} />
              
              <Route path="/customers" element={<BackofficeRoute><Customers /></BackofficeRoute>} />
              <Route path="/customers/:id" element={<BackofficeRoute><CustomerProfile /></BackofficeRoute>} />
              <Route path="/audit" element={<BackofficeRoute><AuditLogs /></BackofficeRoute>} />
              <Route path="/tickets" element={<BackofficeRoute><Tickets /></BackofficeRoute>} />
              <Route path="/marketing" element={<BackofficeRoute><MarketingPage /></BackofficeRoute>} />


              {/* POS routes — also require active merchant */}
              <Route path="/pos" element={<ProtectedRoute><ActiveMerchantRoute><POSHome /></ActiveMerchantRoute></ProtectedRoute>} />
              <Route path="/pos/cart" element={<ProtectedRoute><ActiveMerchantRoute><POSCart /></ActiveMerchantRoute></ProtectedRoute>} />
              <Route path="/pos/checkout" element={<ProtectedRoute><ActiveMerchantRoute><Checkout /></ActiveMerchantRoute></ProtectedRoute>} />
              <Route path="/pos/receipt" element={<ProtectedRoute><ActiveMerchantRoute><Receipt /></ActiveMerchantRoute></ProtectedRoute>} />
              <Route path="/pos/shift" element={<ProtectedRoute><ActiveMerchantRoute><Shift /></ActiveMerchantRoute></ProtectedRoute>} />
              <Route path="/pos/murabaha" element={<ProtectedRoute><ActiveMerchantRoute><NonEmptyCartRoute><MurabahaFlow /></NonEmptyCartRoute></ActiveMerchantRoute></ProtectedRoute>} />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminAuthProvider><AdminLogin /></AdminAuthProvider>} />
              <Route path="/admin/dashboard" element={<AdminAuthProvider><AdminLayout><AdminDashboard /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/merchants" element={<AdminAuthProvider><AdminLayout><AdminMerchants /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/merchants/new" element={<AdminAuthProvider><AdminLayout><AdminMerchantCreate /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/merchants/:id" element={<AdminAuthProvider><AdminLayout><AdminMerchantDetail /></AdminLayout></AdminAuthProvider>} />
              
              
              <Route path="/admin/products" element={<AdminAuthProvider><AdminLayout><AdminProducts /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/pricing" element={<AdminAuthProvider><AdminLayout><AdminPricing /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/invoices" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'finance_admin', 'auditor']}><AdminInvoices /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/fiscal-receipts" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'finance_admin', 'auditor']}><AdminFiscalReceipts /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/merchant-payouts" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'finance_admin', 'auditor']}><AdminMerchantPayouts /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/contracts" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminContracts /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/contracts/:id" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminContractDetail /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />

              <Route path="/admin/tickets" element={<AdminAuthProvider><AdminLayout><AdminTickets /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/tickets/:id" element={<AdminAuthProvider><AdminLayout><AdminTicketDetail /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/reports" element={<AdminAuthProvider><AdminLayout><AdminReports /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/settings" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'finance_admin']}><AdminSettings /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/platform-users" element={<AdminAuthProvider><AdminLayout><AdminPlatformUsers /></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/errors" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin']}><AdminErrors /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />

              {/* Marketing (Super Admin) */}
              <Route path="/admin/marketing" element={<Navigate to="/admin/marketing/dashboard" replace />} />
              <Route path="/admin/marketing/dashboard" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingDashboard /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/campaigns" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingCampaigns /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/campaigns/new" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin']}><AdminMarketingCampaignNew /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/placements/:slug" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingPlacement /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/calendar" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingCalendar /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/history" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingHistory /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/settings" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingSettings /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/campaigns/:id" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin', 'finance_admin', 'auditor']}><AdminMarketingCampaignDetail /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/applications" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'support_admin']}><AdminMarketingApplications /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />
              <Route path="/admin/marketing/cashback" element={<AdminAuthProvider><AdminLayout><RequireAdminRole roles={['platform_owner', 'finance_admin']}><AdminMarketingCashback /></RequireAdminRole></AdminLayout></AdminAuthProvider>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
